/**
 * apps/battle/client/src/PortalBattleScreen.tsx — Portal battle screen.
 *
 * Mirrors BattleScreen.tsx but for portal questions served from
 * GET /api/portal/:portalKey/next. Submits via POST /api/battle/submit
 * (same endpoint as regular battles; portal templates flow through it).
 *
 * Props:
 *   portalKey  — e.g. 'M-Q'
 *   onBack     — called with (xpAwarded, newLevel) to return to map
 */
import React, { useState, useEffect } from 'react';
import { api, ApiError } from './api.js';
import type { Question, GradeResult, SubmitResp } from './api.js';
import { Spinner, ErrorBanner } from './widgets.js';
import { badgeName } from './terminal/config.js';

interface PortalBattleScreenProps {
  portalKey: string;
  onBack: (xpAwarded: number, newLevel: number) => void;
}

interface PortalProgress {
  tier: number;
  passBar: number;
  // M9-patch3: cluster-style N/M (replaces legacy clearedOnce + deployCount).
  solved: number;
  total: number;
}

interface XpBurstState {
  id: number;
  xp: number;
}

function AoChips({ result }: { result: GradeResult }) {
  const chips: { label: string; value: number | undefined }[] = [
    { label: 'AO1', value: result.ao1 },
    { label: 'AO2', value: result.ao2 },
    { label: 'AO3', value: result.ao3 },
    { label: 'AO4', value: result.ao4 },
  ].filter((c) => c.value !== undefined);

  if (chips.length === 0) return null;

  return (
    <div className="ao-chips mt-3">
      {chips.map((c) => (
        <span key={c.label} className="ao-chip">
          {c.label}: {c.value}
        </span>
      ))}
      {(result.ao2Level ?? result.ao3Level ?? result.ao4Level) !== undefined && (
        <span className="ao-chip" style={{ color: 'var(--violet)' }}>
          L{result.ao2Level ?? result.ao3Level ?? result.ao4Level}
        </span>
      )}
      {(result.chainStepsDetected ?? 0) > 0 && (
        <span className="ao-chip" style={{ color: 'var(--success)' }}>
          ⛓ Chain×{result.chainStepsDetected}
        </span>
      )}
      {result.conditionStmtPresent && (
        <span className="ao-chip" style={{ color: 'var(--warning)' }}>
          ⚖ Conditional
        </span>
      )}
    </div>
  );
}

export function PortalBattleScreen({ portalKey, onBack }: PortalBattleScreenProps) {
  const [question, setQuestion] = useState<Question | null>(null);
  const [attemptNumber, setAttemptNumber] = useState(1);
  const [maxAttempts, setMaxAttempts] = useState(3);
  const [portalProgress, setPortalProgress] = useState<PortalProgress | null>(null);
  const [answer, setAnswer] = useState('');
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitResp, setSubmitResp] = useState<SubmitResp | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [xpBursts, setXpBursts] = useState<XpBurstState[]>([]);
  const [showLevelUp, setShowLevelUp] = useState(false);
  const [levelUpText, setLevelUpText] = useState('');
  const [justDeployed, setJustDeployed] = useState(false);
  const burstCounter = React.useRef(0);

  useEffect(() => {
    loadQuestion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portalKey]);

  async function loadQuestion(retryTemplateId?: string) {
    setLoading(true);
    setError(null);
    setQuestion(null);
    setSubmitResp(null);
    setAnswer('');
    setSelectedOption(null);
    setJustDeployed(false);
    try {
      const qs = new URLSearchParams();
      if (retryTemplateId) qs.set('retry', retryTemplateId);
      const url = `/api/portal/${encodeURIComponent(portalKey)}/next${qs.toString() ? `?${qs.toString()}` : ''}`;
      const resp = await api.get<{
        question: Question;
        attemptNumber: number;
        maxAttempts: number;
        portalProgress: PortalProgress;
      }>(url);
      setQuestion(resp.question);
      setAttemptNumber(resp.attemptNumber);
      setMaxAttempts(resp.maxAttempts);
      setPortalProgress(resp.portalProgress);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setError('No portal questions available for this portal yet. Check back soon!');
      } else if (err instanceof ApiError && err.status === 409) {
        setError('You have completed every question in this portal. Well done!');
      } else {
        setError('Failed to load portal question. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit() {
    if (!question) return;
    const ans = question.options ? (selectedOption ?? '') : answer.trim();
    if (!ans) return;

    setSubmitting(true);
    setError(null);
    try {
      const resp = await api.post<SubmitResp>('/api/battle/submit', {
        templateId: question.id,
        answer: ans,
        timeOnQuestionS: 30,
      });
      setSubmitResp(resp);

      // M9-patch3: detect first portal deploy by comparing solved before/after.
      // "First deploy" = solved jumped from 0 → 1 on this submit. This stays
      // strict (no false positives on retries that don't pass the bar) and the
      // server's portalDeployed flag confirms a real deploy occurred.
      const wasFirstDeploy =
        resp.gradeResult.cleared &&
        resp.portalDeployed &&
        portalProgress !== null &&
        portalProgress.solved === 0;
      if (wasFirstDeploy) {
        setJustDeployed(true);
      }
      // Always refresh local solved/total after a successful clear so the
      // status line shows the new N/M without waiting for /api/map.
      if (resp.gradeResult.cleared && portalProgress) {
        // Bump solved by 1 (bounded by total). Be defensive: if the same
        // template was already passed previously, server-side solved counts
        // DISTINCT template_ids so this client-side bump may over-count by 1
        // until the next /api/portal/:key/next call corrects it — which is
        // acceptable for the celebration moment. The next round-trip wins.
        setPortalProgress((prev) => prev ? {
          ...prev,
          solved: Math.min(prev.solved + 1, prev.total),
        } : prev);
      }

      // XP burst animation.
      if (resp.xpAwarded > 0) {
        const id = ++burstCounter.current;
        setXpBursts((prev) => [...prev, { id, xp: resp.xpAwarded }]);
        setTimeout(() => setXpBursts((prev) => prev.filter((b) => b.id !== id)), 1300);
      }

      // Level-up banner.
      if (resp.newLevel > resp.previousLevel) {
        setLevelUpText(`Level Up! → Lv ${resp.newLevel} 🎉`);
        setShowLevelUp(true);
        setTimeout(() => setShowLevelUp(false), 2200);
      }

      setAttemptNumber(resp.attemptNumber);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <Spinner size={28} />
      </div>
    );
  }

  if (error && !question) {
    return (
      <div style={{ padding: '1.25rem' }}>
        <ErrorBanner msg={error} />
        <button className="btn btn-secondary mt-4" data-testid="button-back" onClick={() => onBack(0, 1)}>
          ← Back to Map
        </button>
      </div>
    );
  }

  const isMcq = !!question?.options?.length;
  const result = submitResp?.gradeResult;
  const canRetry = submitResp && !submitResp.isFinalAttempt && !submitResp.gradeResult.cleared;
  // const isResolved = submitResp && (submitResp.gradeResult.cleared || submitResp.isFinalAttempt);
  const tier = portalProgress?.tier ?? question?.markValue ?? '?';
  const passBar = portalProgress?.passBar ?? '?';
  // M9-patch3: cluster-style progress display.
  const solved = portalProgress?.solved ?? 0;
  const total = portalProgress?.total ?? 0;
  const fullyCleared = total > 0 && solved >= total;

  return (
    <div style={{ padding: '1rem', maxWidth: 640, margin: '0 auto' }}>
      {/* XP bursts */}
      {xpBursts.map((b) => (
        <div key={b.id} className="xp-burst">+{b.xp} XP</div>
      ))}
      {showLevelUp && <div className="levelup-banner">{levelUpText}</div>}

      {/* Portal deployed celebration */}
      {justDeployed && (
        <div style={{
          padding: '0.75rem 1rem',
          background: 'rgba(34,197,94,0.12)',
          border: '1px solid rgba(34,197,94,0.4)',
          borderRadius: 8,
          marginBottom: '0.75rem',
          color: 'var(--success)',
          fontWeight: 700,
        }}>
          Portal deployed! ⚡ +bonus XP
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mt-2 mb-4">
        <button className="btn btn-secondary" data-testid="button-back" onClick={() => onBack(submitResp?.xpAwarded ?? 0, submitResp?.newLevel ?? 1)}>
          ← Map
        </button>
        <div className="text-sm text-muted">
          Portal {portalKey} · {tier}m · Pass bar {passBar} · Attempt {attemptNumber}/{maxAttempts}
        </div>
      </div>

      {/* Portal status line — cluster-style N/M progress.
          M9-patch3: shows "N/M cleared" like clusters. When fully cleared,
          highlight in success colour; retries still earn XP per the grader. */}
      <div style={{
        marginBottom: '0.75rem',
        fontSize: '0.85rem',
        color: fullyCleared ? 'var(--success)' : 'var(--text-muted)',
        fontWeight: fullyCleared ? 600 : 400,
      }}>
        {fullyCleared
          ? `✓ Portal completely cleared (${solved}/${total}) — retry any question to farm bonus XP`
          : `${solved}/${total} cleared — each new clear deploys the portal`}
      </div>

      {question && (
        <div className="card">
          <p style={{ color: 'var(--text)', marginBottom: '1rem', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
            {question.stem}
          </p>

          {/* Answer area */}
          {!result && (
            <>
              {isMcq ? (
                <div className="mcq-options">
                  {question.options!.map((opt) => (
                    <button
                      key={opt.option}
                      className={`mcq-option ${selectedOption === opt.option ? 'selected' : ''}`}
                      data-testid={`option-${opt.option}`}
                      onClick={() => setSelectedOption(opt.option)}
                      disabled={submitting}
                    >
                      <strong>{opt.option}.</strong> {opt.text}
                    </button>
                  ))}
                </div>
              ) : (
                <textarea
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  placeholder="Write your answer here…"
                  disabled={submitting}
                  data-testid="input-answer"
                  style={{ marginBottom: '0.75rem' }}
                />
              )}
              {error && <ErrorBanner msg={error} />}
              <button
                className="btn btn-primary w-full mt-3"
                data-testid="button-submit"
                onClick={handleSubmit}
                disabled={submitting || (isMcq ? !selectedOption : !answer.trim())}
              >
                {submitting ? <Spinner size={16} /> : 'Submit Answer'}
              </button>
            </>
          )}

          {/* Feedback panel */}
          {result && submitResp && (
            <div className="feedback-panel">
              <div className="flex items-center gap-2">
                <span className={`score-badge ${result.cleared ? 'cleared' : 'failed'}`}>
                  {result.score}/{result.marks}
                </span>
                {result.cleared && <span style={{ color: 'var(--success)', fontWeight: 700 }}>Cleared! ✓</span>}
                {result.perfectStrike && (
                  <span style={{ color: 'var(--violet)', fontWeight: 700 }}>Perfect Strike ⭐</span>
                )}
                {submitResp.xpAwarded > 0 && (
                  <span style={{ color: 'var(--accent-light)', fontWeight: 700, marginLeft: 'auto' }}>
                    +{submitResp.xpAwarded} XP
                  </span>
                )}
              </div>

              <AoChips result={result} />

              <p style={{ marginTop: '0.875rem', color: 'var(--text)', lineHeight: 1.6 }}>
                {result.feedback}
              </p>

              {result.modelAnswer && (
                <div className="model-answer-box">
                  <div style={{ fontWeight: 600, marginBottom: '0.375rem', color: 'var(--accent-light)' }}>
                    Model Answer
                  </div>
                  <p style={{ color: 'var(--text)', whiteSpace: 'pre-wrap' }}>{result.modelAnswer}</p>
                </div>
              )}

              {submitResp.newBadges.length > 0 && (
                <div style={{ marginTop: '0.875rem', padding: '0.75rem', background: 'rgba(139,92,246,.12)', borderRadius: 8, border: '1px solid rgba(139,92,246,.3)' }}>
                  <div style={{ fontWeight: 700, color: 'var(--violet)' }}>🏅 Badge Unlocked!</div>
                  {submitResp.newBadges.map((b) => (
                    <div key={b} style={{ color: 'var(--text)', fontSize: '0.875rem' }}>{badgeName(b)}</div>
                  ))}
                </div>
              )}

              <div className="flex gap-2 mt-4">
                {canRetry && (
                  <button
                    className="btn btn-secondary"
                    data-testid="button-retry"
                    onClick={() => loadQuestion(question?.id)}
                  >
                    Retry ({maxAttempts - attemptNumber} left)
                  </button>
                )}
                <button
                  className="btn btn-primary"
                  data-testid="button-next-portal"
                  onClick={() => loadQuestion()}
                >
                  Next portal question →
                </button>
                <button
                  className="btn btn-secondary"
                  data-testid="button-back"
                  onClick={() => onBack(submitResp.xpAwarded, submitResp.newLevel)}
                >
                  ← Back to Map
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
